import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Cloud, Copy, ExternalLink, FileText, Loader2, Send, Unplug, Upload, Check, AlertTriangle,
  History, Twitter, FileDown, Globe2,
} from "lucide-react";
import { mdToHtml } from "./markdown";
import OtterIcon from "@/components/app/OtterIcon";

/**
 * One-click publish surface for the Content Studio editor.
 *
 * - WordPress: connect via Application Password, then publish drafts as
 *   `draft` or `publish` to /wp-json/wp/v2/posts (one click). Connection
 *   data lives on the project, not the user.
 * - Notion / any rich editor: copies the rendered HTML to the clipboard
 *   as `text/html` so a single paste preserves headings, lists, bold etc.
 *
 * Props:
 *  - open / onOpenChange   controlled Dialog state
 *  - projectId             current project id
 *  - draftId               saved draft id (null if user hasn't saved yet)
 *  - title                 current editor title (for the WP "untitled" guard)
 *  - markdown              current editor markdown (used by Notion copy when unsaved)
 */
export default function PublishMenu({
  open, onOpenChange, projectId, draftId, title, markdown,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [wp, setWp] = useState(null); // { connected, site_url, username, masked_password, last_verified_at, wp_user_name }
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ site_url: "", username: "", app_password: "" });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState("draft");
  const [lastPublishUrl, setLastPublishUrl] = useState(null);
  const [copying, setCopying] = useState(false);
  const [history, setHistory] = useState(null); // null=loading, []=empty
  const [blogState, setBlogState] = useState({ busy: false, last: null });

  const loadWp = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations/wordpress`);
      setWp(data);
      // Pre-fill the form so editing an existing connection is one-tap.
      if (data.connected) {
        setForm({ site_url: data.site_url || "", username: data.username || "", app_password: "" });
        setEditMode(false);
      } else {
        setEditMode(true);
      }
    } catch (e) {
      // 404 / network — assume not connected.
      setWp({ connected: false });
      setEditMode(true);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    loadWp();
  }, [open, loadWp]);

  const loadHistory = useCallback(async () => {
    if (!draftId) { setHistory([]); return; }
    setHistory(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/content/drafts/${draftId}/publish/history`,
      );
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    }
  }, [projectId, draftId]);

  useEffect(() => {
    if (!open) return;
    loadHistory();
  }, [open, loadHistory]);

  const logEvent = async (kind, extra = {}) => {
    if (!draftId) return;
    try {
      await api.post(`/projects/${projectId}/content/drafts/${draftId}/publish/log`, {
        kind, ...extra,
      });
      loadHistory();
    } catch { /* non-fatal — history is a nice-to-have */ }
  };

  const saveWp = async () => {
    if (!form.site_url.trim() || !form.username.trim() || !form.app_password.trim()) {
      toast.error("Site URL, username and application password are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/integrations/wordpress`, {
        site_url: form.site_url.trim(),
        username: form.username.trim(),
        app_password: form.app_password.trim(),
      });
      setWp(data);
      setEditMode(false);
      setForm((f) => ({ ...f, app_password: "" }));
      toast.success(`Connected to WordPress as ${data.wp_user_name || data.username}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not connect");
    }
    setSaving(false);
  };

  const disconnectWp = async () => {
    if (!confirm("Disconnect this project from WordPress?")) return;
    try {
      await api.delete(`/projects/${projectId}/integrations/wordpress`);
      setWp({ connected: false });
      setEditMode(true);
      setForm({ site_url: "", username: "", app_password: "" });
      toast.success("Disconnected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const publish = async () => {
    if (!draftId) { toast.error("Save the draft first, then publish."); return; }
    if (!(title || "").trim()) { toast.error("Set a draft title before publishing."); return; }
    setPublishing(true); setLastPublishUrl(null);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/content/drafts/${draftId}/publish/wordpress`,
        { status: publishStatus },
      );
      setLastPublishUrl(data.url);
      toast.success(
        publishStatus === "publish"
          ? "Published live to WordPress"
          : "Saved as draft on WordPress",
      );
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    }
    setPublishing(false);
  };

  const publishToBlog = async () => {
    if (!draftId) { toast.error("Save the draft first."); return; }
    if (!(title || "").trim()) { toast.error("Set a title before publishing."); return; }
    setBlogState((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post(`/projects/${projectId}/content/drafts/${draftId}/publish/blog`);
      setBlogState({ busy: false, last: data });
      toast.success("Published to the ArloSEO blog");
      loadHistory();
    } catch (e) {
      setBlogState((s) => ({ ...s, busy: false }));
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    }
  };

  const copyHtmlForNotion = async () => {
    const html = mdToHtml(markdown || "");
    if (!html.trim()) { toast.error("Nothing to copy yet."); return; }
    setCopying(true);
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([markdown || ""], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback for browsers without ClipboardItem (e.g. older Firefox).
        await navigator.clipboard.writeText(html);
      }
      toast.success("Copied — paste into Notion (or any rich editor)");
      logEvent("notion");
    } catch (e) {
      toast.error("Browser blocked clipboard access");
    }
    setCopying(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none border border-foreground/30 max-h-[85vh] overflow-y-auto" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Send className="w-5 h-5" /> Publish
          </DialogTitle>
          <DialogDescription className="font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground">
            // ship this draft anywhere
          </DialogDescription>
        </DialogHeader>

        {/* --- WordPress section --- */}
        <section className="border border-border bg-white p-4 space-y-3" data-testid="publish-wp-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4" />
              <h4 className="font-display tracking-tight text-base">WordPress</h4>
              {wp?.connected && !editMode && (
                <span className="inline-flex items-center gap-1 font-mono-data text-[10px] uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5" data-testid="publish-wp-status-connected">
                  <Check className="w-3 h-3" /> connected
                </span>
              )}
            </div>
            {wp?.connected && !editMode && (
              <div className="flex items-center gap-1.5">
                <button onClick={() => setEditMode(true)} className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="publish-wp-edit">
                  edit
                </button>
                <span className="text-muted-foreground/40">·</span>
                <button onClick={disconnectWp} className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid="publish-wp-disconnect">
                  <Unplug className="w-3 h-3" /> disconnect
                </button>
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="publish-wp-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}

          {!loading && wp?.connected && !editMode && (
            <div className="space-y-3" data-testid="publish-wp-ready">
              <div className="font-mono-data text-xs text-muted-foreground space-y-0.5">
                <div>site · <span className="text-foreground">{wp.site_url}</span></div>
                <div>user · <span className="text-foreground">{wp.wp_user_name || wp.username}</span></div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={publishStatus} onValueChange={setPublishStatus}>
                  <SelectTrigger className="rounded-none h-10 border-foreground/30 w-40" data-testid="publish-wp-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="draft">Save as draft</SelectItem>
                    <SelectItem value="publish">Publish live</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={publish} disabled={publishing || !draftId} className="rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="publish-wp-go">
                  {publishing
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>
                    : <><Upload className="w-3.5 h-3.5 mr-1.5" /> {publishStatus === "publish" ? "Publish" : "Send draft"}</>}
                </Button>
              </div>
              {!draftId && (
                <p className="text-xs text-amber-700 flex items-start gap-1.5" data-testid="publish-wp-need-save">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Save the draft first, then publish.
                </p>
              )}
              {lastPublishUrl && (
                <a href={lastPublishUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium underline underline-offset-4" data-testid="publish-wp-last-url">
                  <ExternalLink className="w-3 h-3" /> Open on WordPress
                </a>
              )}
            </div>
          )}

          {!loading && (editMode || !wp?.connected) && (
            <div className="space-y-3" data-testid="publish-wp-form">
              <div className="space-y-2">
                <Label className="font-mono-data text-[10px] uppercase tracking-widest">Site URL</Label>
                <Input
                  value={form.site_url}
                  onChange={(e) => setForm({ ...form, site_url: e.target.value })}
                  placeholder="https://yourblog.com"
                  className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                  data-testid="publish-wp-input-site"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-mono-data text-[10px] uppercase tracking-widest">Username</Label>
                  <Input
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="admin"
                    autoComplete="off"
                    className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                    data-testid="publish-wp-input-user"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono-data text-[10px] uppercase tracking-widest">Application password</Label>
                  <Input
                    value={form.app_password}
                    onChange={(e) => setForm({ ...form, app_password: e.target.value })}
                    type="password"
                    placeholder="xxxx xxxx xxxx xxxx"
                    autoComplete="new-password"
                    className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground font-mono-data"
                    data-testid="publish-wp-input-pw"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Generate an Application Password in WordPress · <em>Users → Profile → Application Passwords</em>. It&apos;s revocable and scoped to publishing only.
              </p>
              <div className="flex items-center gap-2">
                <Button onClick={saveWp} disabled={saving} className="rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="publish-wp-save">
                  {saving
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Verifying…</>
                    : <><Check className="w-3.5 h-3.5 mr-1.5" /> Verify &amp; save</>}
                </Button>
                {wp?.connected && (
                  <button onClick={() => { setEditMode(false); setForm((f) => ({ ...f, app_password: "" })); }} className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="publish-wp-cancel">
                    cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* --- ArloSEO blog (admin only) --- */}
        {isAdmin && (
          <section className="border border-foreground bg-foreground text-background p-4 space-y-2" data-testid="publish-blog-section">
            <div className="flex items-center gap-2">
              <OtterIcon size={16} />
              <h4 className="font-display tracking-tight text-base">ArloSEO blog</h4>
              <span className="inline-flex items-center gap-1 font-mono-data text-[10px] uppercase tracking-widest border border-background/30 px-1.5 py-0.5">
                admin
              </span>
            </div>
            <p className="text-xs text-background/70">
              Ship this draft live to <code className="font-mono-data text-[11px]">arloseo.com/blog</code> in one click.
            </p>
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button
                onClick={publishToBlog}
                disabled={blogState.busy || !draftId}
                className="rounded-none h-10 bg-background text-foreground hover:bg-background/90"
                data-testid="publish-blog-go"
              >
                {blogState.busy
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Publishing…</>
                  : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Publish to blog</>}
              </Button>
              {blogState.last?.url && (
                <a
                  href={blogState.last.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium underline underline-offset-4 text-background"
                  data-testid="publish-blog-last-url"
                >
                  <ExternalLink className="w-3 h-3" /> Open {blogState.last.slug}
                </a>
              )}
            </div>
          </section>
        )}

        {/* --- Notion / clipboard section --- */}
        <section className="border border-border bg-white p-4 space-y-2" data-testid="publish-notion-section">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <h4 className="font-display tracking-tight text-base">Notion / other rich editors</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Copies the formatted content as HTML. Paste into Notion, Google Docs, Medium, Ghost, or Linear and headings, lists and bold all carry across.
          </p>
          <Button onClick={copyHtmlForNotion} disabled={copying} variant="outline" className="rounded-none h-10" data-testid="publish-notion-copy">
            {copying
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Copying…</>
              : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy formatted HTML</>}
          </Button>
        </section>

        {/* --- Publish history --- */}
        <PublishHistory history={history} draftId={draftId} />
      </DialogContent>
    </Dialog>
  );
}


const KIND_META = {
  wordpress: { label: "WordPress", Icon: Globe2, tone: "border-blue-200 bg-blue-50 text-blue-700" },
  arloseo_blog: { label: "ArloSEO blog", Icon: FileText, tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  notion:    { label: "Notion / clipboard", Icon: FileText, tone: "border-slate-200 bg-slate-50 text-slate-700" },
  twitter:   { label: "X / Twitter", Icon: Twitter, tone: "border-sky-200 bg-sky-50 text-sky-700" },
  markdown:  { label: "Markdown export", Icon: FileDown, tone: "border-amber-200 bg-amber-50 text-amber-700" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function PublishHistory({ history, draftId }) {
  return (
    <section className="border border-border bg-white p-4 space-y-2" data-testid="publish-history-section">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4" />
        <h4 className="font-display tracking-tight text-base">Publish history</h4>
      </div>
      {!draftId ? (
        <p className="text-xs text-muted-foreground" data-testid="publish-history-need-save">
          Save the draft to start a publish history.
        </p>
      ) : history === null ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="publish-history-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : history.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="publish-history-empty">
          <OtterIcon size={20} className="text-foreground shrink-0" />
          <span>Nothing published yet. Arlo is ready when you are.</span>
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto -mx-1 px-1" data-testid="publish-history-list">
          {history.slice(0, 25).map((h, i) => {
            const meta = KIND_META[h.kind] || { label: h.kind, Icon: ExternalLink, tone: "border-border bg-white text-muted-foreground" };
            const Icon = meta.Icon;
            return (
              <li
                key={`${h.at}-${i}`}
                className="flex items-center gap-2 text-xs border border-border bg-white px-2 py-1.5"
                data-testid={`publish-history-item-${i}`}
              >
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-data text-[10px] uppercase tracking-widest ${meta.tone}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                {h.status && (
                  <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    {h.status}
                  </span>
                )}
                <span className="ml-auto font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                  {timeAgo(h.at)}
                </span>
                {h.url && (
                  <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline underline-offset-4 text-foreground" data-testid={`publish-history-link-${i}`}>
                    open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
